import { useState, useRef, useEffect } from 'react';
import { saveVizionBoard, getVizionBoards } from './db';

const T = {
  bg:'#06060d',s1:'#0d0c18',s2:'#12111e',s3:'#181628',s4:'#1e1c30',
  border:'rgba(255,255,255,0.07)',border2:'rgba(255,255,255,0.13)',
  gold:'#d4a853',gold2:'#f0c878',cream:'#f0ead8',
  text2:'#9890b0',text3:'#55506a',teal:'#1a8a76',
  purple:'#8b5cf6',error:'#ff6b6b',rust:'#e74c3c',
};

const BOARD_TYPES = ['Film','Short Film','Music Video','Commercial','Content/Social','Photography','Theatre','Fashion','Documentary','Animation','Other'];
const PLATFORM_SPECS = {
  'Film (Theatrical)':'2.39:1 · 24fps · DCP',
  'Film (TV/Streaming)':'16:9 · 23.976fps · ProRes',
  'TikTok / Reels':'9:16 · 24-60fps · Under 3 min',
  'YouTube':'16:9 · 24-60fps · Any duration',
  'Instagram Post':'1:1 or 4:5 · 30fps',
  'Commercial (Broadcast)':'16:9 · 29.97fps · Under 60sec',
  'Music Video':'16:9 or 9:16 · 24fps',
  'Photography (Print)':'3:2 · 300dpi minimum',
  'Photography (Digital)':'4:3 or 3:2 · 72dpi web',
};
const SHOT_TYPES = ['WIDE','CLOSE','MEDIUM','TWO-SHOT','INSERT','OTS','POV','AERIAL','TRACKING','HANDHELD','STATIC','PAN','TILT','DOLLY','CRANE','DRONE'];
const CAMERA_MOVES = ['Static','Handheld','Dolly In','Dolly Out','Pan Left','Pan Right','Tilt Up','Tilt Down','Crane Up','Crane Down','Tracking','Steadicam','Drone','Orbit','Push In','Pull Out'];
const LENSES = ['14mm','18mm','24mm','28mm','35mm','50mm','85mm','105mm','135mm','200mm','24-70mm','70-200mm','Macro','Anamorphic'];
const CONTRACT_TYPES = ['SAG Theatrical','SAG Low Budget','SAG Ultra Low Budget','SAG Student Film','SAG Commercial','SAG New Media','SAG Music Video','Non-Union','Taft-Hartley','Other'];
const UNION_STATUS = ['SAG-AFTRA','SAG-Eligible','Non-Union','Taft-Hartley','IATSE','Teamsters','AEA','Non-Applicable'];
const PERMISSIONS = ['Owner','Editor','Contributor','Viewer'];
const APPROVAL_STATUS = ['Pending','Approved','Revision Requested','Rejected'];

const inp = (extra={}) => ({
  background:T.s3,border:`1px solid ${T.border2}`,borderRadius:8,
  padding:'0.5rem 0.75rem',color:T.cream,fontSize:12,outline:'none',
  fontFamily:'inherit',width:'100%',boxSizing:'border-box',...extra
});
const sel = (extra={}) => ({...inp(),cursor:'pointer',...extra});
const Field = ({label:l,children,style={}}) => (
  <div style={{marginBottom:12,...style}}>
    <div style={{fontSize:10,color:T.text3,textTransform:'uppercase',letterSpacing:2,marginBottom:4}}>{l}</div>
    {children}
  </div>
);
const Btn = ({children,onClick,gold,small,style={}}) => (
  <button onClick={onClick} style={{
    background:gold?`linear-gradient(135deg,${T.gold},${T.gold2})`:T.s3,
    border:gold?'none':`1px solid ${T.border2}`,
    borderRadius:8,padding:small?'4px 10px':'0.55rem 1rem',
    color:gold?T.bg:T.cream,fontSize:small?11:13,fontWeight:gold?700:400,
    cursor:'pointer',fontFamily:'inherit',...style
  }}>{children}</button>
);
const Section = ({title,children,action}) => (
  <div style={{marginBottom:20}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
      <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2}}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const INIT_BOARD = {
  title:'Untitled Board',type:'Film',platform:'Film (Theatrical)',
  director:'',location:'',shootDate:'',colorPalette:['#d4a853','#06060d','#f0ead8'],
  spotifyUrl:'',productionNotes:'',safetyNotes:'',accessibilityNotes:'',
  continuityNotes:'',clientStatus:'Pending',
  budget:{total:'',spent:'',currency:'USD',taxIncentive:'',taxLocation:''},
  signatoryStatus:'Non-Signatory',contractType:'Non-Union',
  insuranceCOI:false,insuranceExpiry:'',
  sustainabilityNotes:'',virtualProductionNotes:'',
};

function SketchCanvas({onSave,onSetAsImage}) {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#f0ead8');
  const [size, setSize] = useState(3);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState({x:0,y:0});
  const histRef = useRef([]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches?.[0]||e;
    return {x:touch.clientX-rect.left,y:touch.clientY-rect.top};
  };
  const startDraw = (e) => {
    const pos = getPos(e);
    setDrawing(true); setStartPos(pos);
    const snap = canvasRef.current.toDataURL();
    histRef.current = [...histRef.current.slice(-19), snap];
    if(tool==='pen'||tool==='eraser') {
      const ctx = canvasRef.current.getContext('2d');
      ctx.beginPath(); ctx.moveTo(pos.x,pos.y);
    }
  };
  const draw = (e) => {
    if(!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    if(tool==='pen') {
      ctx.globalCompositeOperation='source-over';
      ctx.strokeStyle=color;ctx.lineWidth=size;ctx.lineCap='round';
      ctx.lineTo(pos.x,pos.y);ctx.stroke();
    } else if(tool==='eraser') {
      ctx.globalCompositeOperation='destination-out';
      ctx.lineWidth=size*4;ctx.lineCap='round';
      ctx.lineTo(pos.x,pos.y);ctx.stroke();
      ctx.globalCompositeOperation='source-over';
    }
  };
  const endDraw = (e) => {
    if(!drawing) return;
    setDrawing(false);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    if(tool==='rect') {
      ctx.strokeStyle=color;ctx.lineWidth=size;
      ctx.strokeRect(startPos.x,startPos.y,pos.x-startPos.x,pos.y-startPos.y);
    } else if(tool==='circle') {
      const rx=Math.abs(pos.x-startPos.x)/2,ry=Math.abs(pos.y-startPos.y)/2;
      const cx=startPos.x+(pos.x-startPos.x)/2,cy=startPos.y+(pos.y-startPos.y)/2;
      ctx.beginPath();ctx.ellipse(cx,cy,rx,ry,0,0,2*Math.PI);
      ctx.strokeStyle=color;ctx.lineWidth=size;ctx.stroke();
    } else if(tool==='arrow') {
      ctx.strokeStyle=color;ctx.lineWidth=size;
      ctx.beginPath();ctx.moveTo(startPos.x,startPos.y);ctx.lineTo(pos.x,pos.y);ctx.stroke();
      const angle=Math.atan2(pos.y-startPos.y,pos.x-startPos.x),len=12;
      ctx.beginPath();
      ctx.moveTo(pos.x,pos.y);ctx.lineTo(pos.x-len*Math.cos(angle-0.4),pos.y-len*Math.sin(angle-0.4));
      ctx.moveTo(pos.x,pos.y);ctx.lineTo(pos.x-len*Math.cos(angle+0.4),pos.y-len*Math.sin(angle+0.4));
      ctx.stroke();
    }
  };
  const undo = () => {
    if(!histRef.current.length) return;
    const prev = histRef.current[histRef.current.length-1];
    histRef.current = histRef.current.slice(0,-1);
    const img = new Image();
    img.onload=()=>{const ctx=canvasRef.current.getContext('2d');ctx.clearRect(0,0,800,400);ctx.drawImage(img,0,0);};
    img.src=prev;
  };
  const clear = () => canvasRef.current.getContext('2d').clearRect(0,0,800,400);
  const saveSketch = () => { onSave(canvasRef.current.toDataURL('image/png')); clear(); };
  const tools = [{id:'pen',icon:'✏️'},{id:'eraser',icon:'⬜'},{id:'rect',icon:'▭'},{id:'circle',icon:'○'},{id:'arrow',icon:'→'}];
  const colors = ['#f0ead8','#d4a853','#1a8a76','#e74c3c','#8b5cf6','#3b82f6','#000000','#ffffff'];
  return (
    <div style={{background:T.s2,borderRadius:12,border:`1px solid ${T.border2}`,overflow:'hidden'}}>
      <div style={{padding:'8px 12px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        {tools.map(t=>(
          <button key={t.id} onClick={()=>setTool(t.id)} style={{background:tool===t.id?T.gold:T.s3,color:tool===t.id?T.bg:T.cream,border:`1px solid ${T.border2}`,borderRadius:6,padding:'4px 8px',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>{t.icon}</button>
        ))}
        <div style={{width:1,height:20,background:T.border2}}/>
        {colors.map(c=>(
          <div key={c} onClick={()=>setColor(c)} style={{width:18,height:18,borderRadius:3,background:c,cursor:'pointer',border:color===c?`2px solid ${T.gold}`:`1px solid ${T.border2}`}}/>
        ))}
        <div style={{width:1,height:20,background:T.border2}}/>
        <input type="range" min={1} max={12} value={size} onChange={e=>setSize(+e.target.value)} style={{width:60,accentColor:T.gold}}/>
        <Btn small onClick={undo}>↩</Btn>
        <Btn small onClick={clear}>Clear</Btn>
        <Btn small gold onClick={saveSketch}>Save Sketch</Btn>
        <Btn small onClick={()=>onSetAsImage(canvasRef.current.toDataURL('image/png'))}>Set as Shot Image</Btn>
      </div>
      <canvas ref={canvasRef} width={800} height={400}
        style={{display:'block',background:'#111',width:'100%',cursor:tool==='eraser'?'cell':'crosshair',touchAction:'none'}}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}/>
    </div>
  );
}

function SagTab({board,setBoard}) {
  const [cast,setCast] = useState([]);
  const add = () => setCast(c=>[...c,{id:Date.now(),name:'',role:'',unionStatus:'Non-Union',contractType:'Non-Union',isTaftHartley:false,taftReason:'',dealMemo:'',exhibitA:false,notes:''}]);
  const upd = (id,f,v) => setCast(c=>c.map(m=>m.id===id?{...m,[f]:v}:m));
  const rem = (id) => setCast(c=>c.filter(m=>m.id!==id));
  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        <Field label="Production Signatory Status">
          <select value={board.signatoryStatus||'Non-Signatory'} onChange={e=>setBoard(b=>({...b,signatoryStatus:e.target.value}))} style={sel()}>
            {['Non-Signatory','SAG-AFTRA Signatory (In Process)','SAG-AFTRA Signatory (Approved)','Student Film Agreement'].map(s=><option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Primary Contract Type">
          <select value={board.contractType||'Non-Union'} onChange={e=>setBoard(b=>({...b,contractType:e.target.value}))} style={sel()}>
            {CONTRACT_TYPES.map(c=><option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Certificate of Insurance">
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <input type="checkbox" checked={!!board.insuranceCOI} onChange={e=>setBoard(b=>({...b,insuranceCOI:e.target.checked}))} style={{accentColor:T.gold}}/>
            <span style={{color:T.text2,fontSize:12}}>COI Obtained</span>
            <input type="date" value={board.insuranceExpiry||''} onChange={e=>setBoard(b=>({...b,insuranceExpiry:e.target.value}))} style={{...inp(),width:'auto',flex:1}}/>
          </div>
        </Field>
        <Field label="Tax Incentive Location">
          <input value={board.budget?.taxLocation||''} onChange={e=>setBoard(b=>({...b,budget:{...b.budget,taxLocation:e.target.value}}))} style={inp()} placeholder="State/City"/>
        </Field>
      </div>
      <Section title={`Cast & Crew (${cast.length})`} action={<Btn small gold onClick={add}>+ Add Member</Btn>}>
        {cast.length===0 && <div style={{color:T.text3,fontSize:12,padding:'1rem',textAlign:'center'}}>No cast or crew added yet.</div>}
        {cast.map(m=>(
          <div key={m.id} style={{background:T.s2,border:`1px solid ${m.isTaftHartley?T.rust:T.border2}`,borderRadius:10,padding:'12px',marginBottom:10}}>
            {m.isTaftHartley && <div style={{background:'rgba(231,76,60,0.12)',color:T.rust,fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:4,marginBottom:8,display:'inline-block'}}>⚠ TAFT-HARTLEY — Reason for hire required</div>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8}}>
              <input value={m.name} onChange={e=>upd(m.id,'name',e.target.value)} style={inp()} placeholder="Name"/>
              <input value={m.role} onChange={e=>upd(m.id,'role',e.target.value)} style={inp()} placeholder="Role/Position"/>
              <select value={m.unionStatus} onChange={e=>{upd(m.id,'unionStatus',e.target.value);upd(m.id,'isTaftHartley',e.target.value==='Taft-Hartley');}} style={sel()}>
                {UNION_STATUS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
              <select value={m.contractType} onChange={e=>upd(m.id,'contractType',e.target.value)} style={sel()}>
                {CONTRACT_TYPES.map(c=><option key={c}>{c}</option>)}
              </select>
              <div style={{display:'flex',gap:12,alignItems:'center'}}>
                <label style={{display:'flex',gap:6,alignItems:'center',fontSize:12,color:T.text2,cursor:'pointer'}}>
                  <input type="checkbox" checked={!!m.exhibitA} onChange={e=>upd(m.id,'exhibitA',e.target.checked)} style={{accentColor:T.gold}}/>Exhibit A
                </label>
                <label style={{display:'flex',gap:6,alignItems:'center',fontSize:12,color:T.text2,cursor:'pointer'}}>
                  <input type="checkbox" checked={!!m.isTaftHartley} onChange={e=>upd(m.id,'isTaftHartley',e.target.checked)} style={{accentColor:T.rust}}/>Taft-Hartley
                </label>
              </div>
            </div>
            {m.isTaftHartley && <textarea value={m.taftReason} onChange={e=>upd(m.id,'taftReason',e.target.value)} style={{...inp(),height:60,resize:'vertical',marginBottom:8}} placeholder="Reason for hiring non-union performer (required for SAG-AFTRA submission)"/>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <input value={m.dealMemo} onChange={e=>upd(m.id,'dealMemo',e.target.value)} style={inp()} placeholder="Deal memo / rate"/>
              <input value={m.notes} onChange={e=>upd(m.id,'notes',e.target.value)} style={inp()} placeholder="Notes"/>
            </div>
            <div style={{textAlign:'right',marginTop:8}}><Btn small onClick={()=>rem(m.id)} style={{color:T.error,borderColor:T.error}}>Remove</Btn></div>
          </div>
        ))}
      </Section>
    </div>
  );
}

function TeamTab({board,setBoard}) {
  const [team,setTeam] = useState([{id:1,handle:'@you',role:'Director',permission:'Owner',dealMemo:'',signed:true}]);
  const add = () => setTeam(t=>[...t,{id:Date.now(),handle:'',role:'',permission:'Contributor',dealMemo:'',signed:false}]);
  const upd = (id,f,v) => setTeam(t=>t.map(m=>m.id===id?{...m,[f]:v}:m));
  return (
    <div>
      <Section title="Board Ownership & Permissions" action={<Btn small gold onClick={add}>+ Invite via @handle</Btn>}>
        {team.map(m=>(
          <div key={m.id} style={{background:T.s2,border:`1px solid ${m.permission==='Owner'?T.gold:T.border2}`,borderRadius:10,padding:'12px',marginBottom:10}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr auto',gap:8,alignItems:'center'}}>
              <input value={m.handle} onChange={e=>upd(m.id,'handle',e.target.value)} style={inp()} placeholder="@artizan-handle"/>
              <input value={m.role} onChange={e=>upd(m.id,'role',e.target.value)} style={inp()} placeholder="Role"/>
              <select value={m.permission} onChange={e=>upd(m.id,'permission',e.target.value)} style={sel()} disabled={m.permission==='Owner'}>
                {PERMISSIONS.map(p=><option key={p}>{p}</option>)}
              </select>
              <input value={m.dealMemo} onChange={e=>upd(m.id,'dealMemo',e.target.value)} style={inp()} placeholder="Deal memo / rate"/>
              <label style={{fontSize:11,color:T.text2,display:'flex',gap:4,alignItems:'center',cursor:'pointer',whiteSpace:'nowrap'}}>
                <input type="checkbox" checked={!!m.signed} onChange={e=>upd(m.id,'signed',e.target.checked)} style={{accentColor:T.gold}}/>Signed
              </label>
            </div>
          </div>
        ))}
      </Section>
      <div style={{background:T.s2,borderRadius:10,padding:'1rem',border:`1px solid ${T.border}`,fontSize:11,color:T.text2,lineHeight:1.8}}>
        <div style={{fontWeight:700,color:T.gold,marginBottom:6}}>Permission Levels</div>
        <div><strong style={{color:T.cream}}>Owner</strong> — Full control. Delete board, manage all permissions, transfer ownership.</div>
        <div><strong style={{color:T.cream}}>Editor</strong> — Change anything except delete board or manage ownership.</div>
        <div><strong style={{color:T.cream}}>Contributor</strong> — Add/edit shots and scenes only.</div>
        <div><strong style={{color:T.cream}}>Viewer</strong> — Read-only. Cannot make changes.</div>
      </div>
    </div>
  );
}

function OverviewTab({board,setBoard,scenes}) {
  const totalShots = scenes.reduce((a,s)=>a+s.shots.length,0);
  const upd = (f,v) => setBoard(b=>({...b,[f]:v}));
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
      <div>
        <Section title="Board Info">
          <Field label="Board Title"><input value={board.title} onChange={e=>upd('title',e.target.value)} style={inp()}/></Field>
          <Field label="Board Type">
            <select value={board.type} onChange={e=>upd('type',e.target.value)} style={sel()}>
              {BOARD_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Platform / Deliverable">
            <select value={board.platform} onChange={e=>upd('platform',e.target.value)} style={sel()}>
              {Object.keys(PLATFORM_SPECS).map(p=><option key={p}>{p}</option>)}
            </select>
            {board.platform && <div style={{fontSize:10,color:T.teal,marginTop:4}}>{PLATFORM_SPECS[board.platform]}</div>}
          </Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <Field label="Director / Owner"><input value={board.director} onChange={e=>upd('director',e.target.value)} style={inp()} placeholder="Name or @handle"/></Field>
            <Field label="Shoot Date"><input type="date" value={board.shootDate} onChange={e=>upd('shootDate',e.target.value)} style={inp()}/></Field>
          </div>
          <Field label="Primary Location"><input value={board.location} onChange={e=>upd('location',e.target.value)} style={inp()} placeholder="City, State or Address"/></Field>
        </Section>
        <Section title="Music / Temp Track">
          <Field label="Spotify URL">
            <input value={board.spotifyUrl} onChange={e=>upd('spotifyUrl',e.target.value)} style={inp()} placeholder="https://open.spotify.com/..."/>
          </Field>
          {board.spotifyUrl && (
            <iframe src={board.spotifyUrl.replace('open.spotify.com/track/','open.spotify.com/embed/track/').replace('open.spotify.com/playlist/','open.spotify.com/embed/playlist/')}
              width="100%" height="80" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              style={{borderRadius:8,marginTop:4}}/>
          )}
        </Section>
      </div>
      <div>
        <Section title="Board Stats">
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            {[['Scenes',scenes.length],['Shots',totalShots],['Type',board.type],['Platform',board.platform?.split(' (')[0]||'—']].map(([k,v])=>(
              <div key={k} style={{background:T.s3,border:`1px solid ${T.border}`,borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:10,color:T.text3,textTransform:'uppercase',letterSpacing:2}}>{k}</div>
                <div style={{fontSize:18,fontWeight:700,color:T.gold,marginTop:2}}>{v}</div>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Color Palette">
          <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
            {(board.colorPalette||[]).map((c,i)=>(
              <div key={i} style={{display:'flex',flexDirection:'column',gap:4,alignItems:'center'}}>
                <input type="color" value={c} onChange={e=>upd('colorPalette',board.colorPalette.map((x,j)=>j===i?e.target.value:x))} style={{width:36,height:36,border:'none',background:'none',cursor:'pointer',borderRadius:6}}/>
                <div style={{fontSize:9,color:T.text3}}>{c}</div>
              </div>
            ))}
            {(board.colorPalette||[]).length<8 && <Btn small onClick={()=>upd('colorPalette',[...(board.colorPalette||[]),'#ffffff'])}>+</Btn>}
          </div>
        </Section>
        <Section title="Production Notes">
          <Field label="General Notes"><textarea value={board.productionNotes} onChange={e=>upd('productionNotes',e.target.value)} style={{...inp(),height:60,resize:'vertical'}} placeholder="Notes for the entire production..."/></Field>
          <Field label="Safety Notes"><textarea value={board.safetyNotes} onChange={e=>upd('safetyNotes',e.target.value)} style={{...inp(),height:50,resize:'vertical'}} placeholder="Stunts, hazards, special conditions..."/></Field>
          <Field label="Accessibility Planning"><textarea value={board.accessibilityNotes} onChange={e=>upd('accessibilityNotes',e.target.value)} style={{...inp(),height:50,resize:'vertical'}} placeholder="Captions, audio description..."/></Field>
          <Field label="Sustainability / Green Production"><textarea value={board.sustainabilityNotes} onChange={e=>upd('sustainabilityNotes',e.target.value)} style={{...inp(),height:50,resize:'vertical'}} placeholder="Green production practices..."/></Field>
          <Field label="Virtual Production Notes"><textarea value={board.virtualProductionNotes} onChange={e=>upd('virtualProductionNotes',e.target.value)} style={{...inp(),height:50,resize:'vertical'}} placeholder="LED volume specs, game engine notes..."/></Field>
        </Section>
      </div>
    </div>
  );
}

function ScenesTab({scenes,setScenes}) {
  const [activeScene,setActiveScene] = useState(0);
  const [activeShot,setActiveShot] = useState(0);
  const [showSketch,setShowSketch] = useState(false);
  const scene = scenes[activeScene];
  const shot = scene?.shots[activeShot];
  const updScene = (f,v) => setScenes(ss=>ss.map((s,i)=>i===activeScene?{...s,[f]:v}:s));
  const updShot = (f,v) => setScenes(ss=>ss.map((s,i)=>i!==activeScene?s:{...s,shots:s.shots.map((sh,j)=>j!==activeShot?sh:{...sh,[f]:v})}));
  const mkShot = () => ({id:`sh${Date.now()}`,num:'01',type:'WIDE',desc:'',tags:[],notes:'',cameraMove:'Static',lens:'35mm',iso:'',aperture:'',shutter:'',lighting:'',materials:'',positioning:'',vfxNotes:'',clientStatus:'Pending',inspirationUrls:[],sketches:[],sketchAsImage:null});
  const mkScene = (n) => ({id:`s${Date.now()}`,num:String(n).padStart(2,'0'),name:'INT/EXT. LOCATION — DAY',characters:[],set:'',props:[],lighting:'',hairMakeup:'',wardrobe:'',weather:'',vfxNotes:'',musicNotes:'',continuityFlags:'',clientStatus:'Pending',inspirationUrls:[],shots:[mkShot()]});
  const addScene = () => { setScenes(ss=>[...ss,mkScene(ss.length+1)]); setActiveScene(scenes.length); setActiveShot(0); };
  const addShot = () => { setScenes(ss=>ss.map((s,i)=>i===activeScene?{...s,shots:[...s.shots,{...mkShot(),num:String(s.shots.length+1).padStart(2,'0')}]}:s)); setActiveShot((scene?.shots.length)||0); };
  const saveSketch = (d) => updShot('sketches',[...(shot?.sketches||[]),{id:Date.now(),data:d,label:'Sketch '+((shot?.sketches||[]).length+1)}]);

  return (
    <div style={{display:'grid',gridTemplateColumns:'200px 1fr',height:'72vh',overflow:'hidden'}}>
      <div style={{borderRight:`1px solid ${T.border}`,overflowY:'auto'}}>
        <div style={{padding:'8px',borderBottom:`1px solid ${T.border}`}}>
          <Btn small gold onClick={addScene} style={{width:'100%'}}>+ Scene</Btn>
        </div>
        {scenes.length===0 && <div style={{padding:'1rem',color:T.text3,fontSize:12,textAlign:'center'}}>No scenes yet</div>}
        {scenes.map((s,i)=>(
          <div key={s.id} onClick={()=>{setActiveScene(i);setActiveShot(0);}} style={{padding:'8px 10px',cursor:'pointer',borderBottom:`1px solid ${T.border}`,background:i===activeScene?T.s3:'transparent'}}>
            <div style={{fontSize:10,color:T.gold,fontWeight:700}}>SCENE {s.num}</div>
            <div style={{fontSize:11,color:T.cream,lineHeight:1.4,marginTop:2}}>{s.name}</div>
            <div style={{fontSize:10,color:T.text3,marginTop:2}}>{s.shots.length} shots</div>
          </div>
        ))}
      </div>
      <div style={{overflowY:'auto',padding:'16px'}}>
        {!scene ? (
          <div style={{textAlign:'center',paddingTop:'3rem'}}>
            <div style={{color:T.text3,fontSize:13,marginBottom:16}}>No scenes yet. Add your first scene to get started.</div>
            <Btn gold onClick={addScene}>+ Add First Scene</Btn>
          </div>
        ) : (
          <>
            <div style={{marginBottom:16,padding:'12px',background:T.s2,borderRadius:10,border:`1px solid ${T.border2}`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div style={{fontSize:12,fontWeight:700,color:T.gold}}>SCENE {scene.num}</div>
                <select value={scene.clientStatus||'Pending'} onChange={e=>updScene('clientStatus',e.target.value)} style={{...sel(),width:'auto',fontSize:11}}>
                  {APPROVAL_STATUS.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <input value={scene.name} onChange={e=>updScene('name',e.target.value)} style={{...inp(),fontSize:13,fontWeight:600,marginBottom:10}} placeholder="Scene heading"/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <Field label="Characters / Roles"><input value={(scene.characters||[]).join(', ')} onChange={e=>updScene('characters',e.target.value.split(',').map(x=>x.trim()).filter(Boolean))} style={inp()} placeholder="Marcus, Elena, BG x3..."/></Field>
                <Field label="Set Description"><input value={scene.set||''} onChange={e=>updScene('set',e.target.value)} style={inp()} placeholder="Location details..."/></Field>
                <Field label="Props"><input value={(scene.props||[]).join(', ')} onChange={e=>updScene('props',e.target.value.split(',').map(x=>x.trim()).filter(Boolean))} style={inp()} placeholder="Gate handle, lamp..."/></Field>
                <Field label="Lighting Plan"><input value={scene.lighting||''} onChange={e=>updScene('lighting',e.target.value)} style={inp()} placeholder="Available, practicals..."/></Field>
                <Field label="Hair & Makeup"><input value={scene.hairMakeup||''} onChange={e=>updScene('hairMakeup',e.target.value)} style={inp()} placeholder="Continuity, SFX makeup..."/></Field>
                <Field label="Wardrobe"><input value={scene.wardrobe||''} onChange={e=>updScene('wardrobe',e.target.value)} style={inp()} placeholder="Character wardrobe..."/></Field>
                <Field label="Weather / Location Notes"><input value={scene.weather||''} onChange={e=>updScene('weather',e.target.value)} style={inp()} placeholder="Permits, contingency..."/></Field>
                <Field label="Continuity Flags"><input value={scene.continuityFlags||''} onChange={e=>updScene('continuityFlags',e.target.value)} style={inp()} placeholder="Match from Scene X..."/></Field>
                <Field label="VFX / Post Notes"><input value={scene.vfxNotes||''} onChange={e=>updScene('vfxNotes',e.target.value)} style={inp()} placeholder="Green screen, comp..."/></Field>
                <Field label="Music / Sound Notes"><input value={scene.musicNotes||''} onChange={e=>updScene('musicNotes',e.target.value)} style={inp()} placeholder="Temp track, mood..."/></Field>
              </div>
            </div>
            <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
              {scene.shots.map((sh,j)=>(
                <button key={sh.id} onClick={()=>setActiveShot(j)} style={{background:j===activeShot?T.gold:T.s3,color:j===activeShot?T.bg:T.cream,border:`1px solid ${j===activeShot?T.gold:T.border2}`,borderRadius:6,padding:'4px 10px',fontSize:11,cursor:'pointer',fontFamily:'inherit',fontWeight:j===activeShot?700:400}}>
                  {sh.num} · {sh.type}
                </button>
              ))}
              <Btn small gold onClick={addShot}>+ Shot</Btn>
            </div>
            {shot && (
              <div style={{background:T.s2,border:`1px solid ${T.border2}`,borderRadius:10,padding:'14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.gold}}>SHOT {shot.num}</div>
                  <select value={shot.clientStatus||'Pending'} onChange={e=>updShot('clientStatus',e.target.value)} style={{...sel(),width:'auto',fontSize:11}}>
                    {APPROVAL_STATUS.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                  <Field label="Shot Type"><select value={shot.type} onChange={e=>updShot('type',e.target.value)} style={sel()}>{SHOT_TYPES.map(t=><option key={t}>{t}</option>)}</select></Field>
                  <Field label="Camera Movement"><select value={shot.cameraMove||'Static'} onChange={e=>updShot('cameraMove',e.target.value)} style={sel()}>{CAMERA_MOVES.map(m=><option key={m}>{m}</option>)}</select></Field>
                  <Field label="Lens"><select value={shot.lens||'35mm'} onChange={e=>updShot('lens',e.target.value)} style={sel()}>{LENSES.map(l=><option key={l}>{l}</option>)}</select></Field>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
                    <Field label="ISO"><input value={shot.iso||''} onChange={e=>updShot('iso',e.target.value)} style={inp()} placeholder="800"/></Field>
                    <Field label="f/stop"><input value={shot.aperture||''} onChange={e=>updShot('aperture',e.target.value)} style={inp()} placeholder="2.8"/></Field>
                    <Field label="Shutter"><input value={shot.shutter||''} onChange={e=>updShot('shutter',e.target.value)} style={inp()} placeholder="1/50"/></Field>
                  </div>
                </div>
                <Field label="Shot Description"><textarea value={shot.desc} onChange={e=>updShot('desc',e.target.value)} style={{...inp(),height:60,resize:'vertical'}} placeholder="What happens in this shot..."/></Field>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <Field label="Lighting Setup"><input value={shot.lighting||''} onChange={e=>updShot('lighting',e.target.value)} style={inp()} placeholder="Key, fill, practical..."/></Field>
                  <Field label="Subject / Talent Positioning"><input value={shot.positioning||''} onChange={e=>updShot('positioning',e.target.value)} style={inp()} placeholder="Blocking, expression..."/></Field>
                  <Field label="Materials Needed"><input value={shot.materials||''} onChange={e=>updShot('materials',e.target.value)} style={inp()} placeholder="Rigs, filters, special equipment..."/></Field>
                  <Field label="VFX Notes"><input value={shot.vfxNotes||''} onChange={e=>updShot('vfxNotes',e.target.value)} style={inp()} placeholder="Post notes for this shot..."/></Field>
                </div>
                <Field label="Director Notes"><textarea value={shot.notes} onChange={e=>updShot('notes',e.target.value)} style={{...inp(),height:50,resize:'vertical'}} placeholder="Special instructions..."/></Field>
                <div style={{marginTop:16}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2}}>Sketches ({(shot.sketches||[]).length})</div>
                    <Btn small onClick={()=>setShowSketch(!showSketch)}>{showSketch?'Hide Canvas':'Open Sketch Canvas'}</Btn>
                  </div>
                  {showSketch && <SketchCanvas onSave={saveSketch} onSetAsImage={(d)=>updShot('sketchAsImage',d)}/>}
                  {(shot.sketches||[]).length>0 && (
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}>
                      {shot.sketches.map((sk)=>(
                        <div key={sk.id} style={{position:'relative'}}>
                          <img src={sk.data} alt={sk.label} style={{width:120,height:80,objectFit:'cover',borderRadius:6,border:`1px solid ${T.border2}`,cursor:'pointer'}} onClick={()=>updShot('sketchAsImage',sk.data)}/>
                          <div style={{fontSize:9,color:T.text3,textAlign:'center',marginTop:2}}>{sk.label}</div>
                          {shot.sketchAsImage===sk.data && <div style={{position:'absolute',top:4,right:4,background:T.teal,borderRadius:3,padding:'2px 4px',fontSize:8,color:'white'}}>SHOT IMG</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ── INSPIRATION TAB ──────────────────────────────────────────
function InspirationTab({board,setBoard}) {
  const [items,setItems] = useState(board.inspirationItems||[]);
  const [newUrl,setNewUrl] = useState('');
  const [newNote,setNewNote] = useState('');
  const [activeType,setActiveType] = useState('all');
  const fileRef = useRef(null);

  const save = (list) => { setItems(list); setBoard(b=>({...b,inspirationItems:list})); };

  const addUrl = () => {
    if(!newUrl.trim()) return;
    const isVideo = newUrl.includes('youtube')||newUrl.includes('vimeo');
    const isSpotify = newUrl.includes('spotify');
    save([...items,{id:Date.now(),type:isVideo?'video':isSpotify?'music':'link',url:newUrl,note:newNote,created:new Date().toISOString()}]);
    setNewUrl('');setNewNote('');
  };

  const addImage = (e) => {
    const file = e.target.files[0];
    if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      save([...items,{id:Date.now(),type:'image',url:ev.target.result,note:'',filename:file.name,created:new Date().toISOString()}]);
    };
    reader.readAsDataURL(file);
  };

  const remove = (id) => save(items.filter(i=>i.id!==id));
  const filtered = activeType==='all'?items:items.filter(i=>i.type===activeType);

  const getEmbed = (url) => {
    if(url.includes('youtube.com/watch?v=')) return url.replace('youtube.com/watch?v=','youtube.com/embed/').split('&')[0];
    if(url.includes('youtu.be/')) return 'https://youtube.com/embed/'+url.split('youtu.be/')[1];
    if(url.includes('vimeo.com/')) return 'https://player.vimeo.com/video/'+url.split('vimeo.com/')[1];
    if(url.includes('open.spotify.com/')) return url.replace('open.spotify.com/','open.spotify.com/embed/');
    return null;
  };

  return (
    <div>
      <div style={{background:T.s2,border:`1px solid ${T.border2}`,borderRadius:12,padding:16,marginBottom:16}}>
        <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2,marginBottom:10}}>Add Inspiration</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:8,marginBottom:8}}>
          <input value={newUrl} onChange={e=>setNewUrl(e.target.value)} style={inp()} placeholder="Paste URL — YouTube, Vimeo, Spotify, Pinterest, any link..."/>
          <Btn gold small onClick={addUrl}>Add</Btn>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:8}}>
          <input value={newNote} onChange={e=>setNewNote(e.target.value)} style={inp()} placeholder="Note (optional) — what does this reference?"/>
          <Btn small onClick={()=>fileRef.current?.click()}>📁 Upload Image</Btn>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={addImage}/>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
        {['all','image','video','music','link'].map(t=>(
          <button key={t} onClick={()=>setActiveType(t)} style={{background:activeType===t?T.gold:T.s3,color:activeType===t?T.bg:T.text2,border:`1px solid ${activeType===t?T.gold:T.border2}`,borderRadius:20,padding:'4px 12px',fontSize:11,cursor:'pointer',fontFamily:'inherit',textTransform:'capitalize',fontWeight:activeType===t?700:400}}>
            {t==='all'?`All (${items.length})`:t}
          </button>
        ))}
      </div>

      {filtered.length===0 && <div style={{padding:'3rem',textAlign:'center',color:T.text3,border:`1px dashed ${T.border2}`,borderRadius:12}}>No inspiration added yet. Paste URLs or upload images to build your visual reference.</div>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
        {filtered.map(item=>(
          <div key={item.id} style={{background:T.s2,border:`1px solid ${T.border}`,borderRadius:12,overflow:'hidden',position:'relative'}}>
            {item.type==='image' && <img src={item.url} alt={item.note} style={{width:'100%',height:160,objectFit:'cover'}}/>}
            {(item.type==='video') && getEmbed(item.url) && (
              <iframe src={getEmbed(item.url)} width="100%" height={160} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen style={{display:'block'}}/>
            )}
            {item.type==='music' && getEmbed(item.url) && (
              <iframe src={getEmbed(item.url)} width="100%" height={80} frameBorder="0" allow="autoplay; clipboard-write; encrypted-media" style={{display:'block'}}/>
            )}
            {item.type==='link' && (
              <a href={item.url} target="_blank" rel="noreferrer" style={{display:'block',padding:'16px',textDecoration:'none'}}>
                <div style={{fontSize:11,color:T.teal,wordBreak:'break-all'}}>{item.url}</div>
              </a>
            )}
            <div style={{padding:'8px 10px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:11,color:T.text2,flex:1}}>{item.note||item.filename||item.type}</div>
              <button onClick={()=>remove(item.id)} style={{background:'none',border:'none',color:T.text3,cursor:'pointer',fontSize:14,padding:'2px 6px'}}>×</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PHOTO SESSION TAB ─────────────────────────────────────────
function PhotoSessionTab({board,setBoard}) {
  const [session,setSession] = useState(board.photoSession||{client:'',location:'',date:'',callTime:'',wrapTime:'',talent:[],outfits:[],equipment:[],locationNotes:'',lightingPlan:'',postNotes:'',deliverables:'',deadline:''});
  const upd = (f,v) => { const s={...session,[f]:v}; setSession(s); setBoard(b=>({...b,photoSession:s})); };
  const addTalent = () => { const t=[...(session.talent||[]),{id:Date.now(),name:'',role:'',callTime:'',notes:''}]; upd('talent',t); };
  const updTalent = (id,f,v) => upd('talent',session.talent.map(t=>t.id===id?{...t,[f]:v}:t));
  const addOutfit = () => upd('outfits',[...(session.outfits||[]),{id:Date.now(),look:'',talent:'',notes:'',order:''}]);
  const updOutfit = (id,f,v) => upd('outfits',session.outfits.map(o=>o.id===id?{...o,[f]:v}:o));

  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
      <div>
        <Section title="Session Info">
          <Field label="Client / Brand"><input value={session.client} onChange={e=>upd('client',e.target.value)} style={inp()}/></Field>
          <Field label="Location"><input value={session.location} onChange={e=>upd('location',e.target.value)} style={inp()} placeholder="Studio, outdoor, address..."/></Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
            <Field label="Date"><input type="date" value={session.date} onChange={e=>upd('date',e.target.value)} style={inp()}/></Field>
            <Field label="Call Time"><input type="time" value={session.callTime} onChange={e=>upd('callTime',e.target.value)} style={inp()}/></Field>
            <Field label="Wrap Time"><input type="time" value={session.wrapTime} onChange={e=>upd('wrapTime',e.target.value)} style={inp()}/></Field>
          </div>
          <Field label="Location Notes"><textarea value={session.locationNotes} onChange={e=>upd('locationNotes',e.target.value)} style={{...inp(),height:60,resize:'vertical'}} placeholder="Parking, access, permits, restrictions..."/></Field>
          <Field label="Lighting Plan"><textarea value={session.lightingPlan} onChange={e=>upd('lightingPlan',e.target.value)} style={{...inp(),height:60,resize:'vertical'}} placeholder="Natural, strobe, continuous, modifiers..."/></Field>
        </Section>
        <Section title="Equipment List">
          <textarea value={session.equipment} onChange={e=>upd('equipment',e.target.value)} style={{...inp(),height:100,resize:'vertical'}} placeholder="Camera body, lenses, lighting, modifiers, stands, cables, cards..."/>
        </Section>
        <Section title="Post-Production">
          <Field label="Post Notes"><textarea value={session.postNotes} onChange={e=>upd('postNotes',e.target.value)} style={{...inp(),height:60,resize:'vertical'}} placeholder="Retouching style, color grade, selects process..."/></Field>
          <Field label="Deliverables"><textarea value={session.deliverables} onChange={e=>upd('deliverables',e.target.value)} style={{...inp(),height:60,resize:'vertical'}} placeholder="e.g. 20 edited JPEGs, 5 retouched finals, RAW files..."/></Field>
          <Field label="Delivery Deadline"><input type="date" value={session.deadline} onChange={e=>upd('deadline',e.target.value)} style={inp()}/></Field>
        </Section>
      </div>
      <div>
        <Section title={`Talent & Crew (${(session.talent||[]).length})`} action={<Btn small gold onClick={addTalent}>+ Add</Btn>}>
          {(session.talent||[]).length===0 && <div style={{color:T.text3,fontSize:12,padding:'1rem',textAlign:'center'}}>No talent added yet.</div>}
          {(session.talent||[]).map(t=>(
            <div key={t.id} style={{background:T.s3,borderRadius:8,padding:'10px',marginBottom:8}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                <input value={t.name} onChange={e=>updTalent(t.id,'name',e.target.value)} style={inp()} placeholder="Name"/>
                <input value={t.role} onChange={e=>updTalent(t.id,'role',e.target.value)} style={inp()} placeholder="Role (Model, MUA, Stylist...)"/>
                <input value={t.callTime} onChange={e=>updTalent(t.id,'callTime',e.target.value)} style={inp()} placeholder="Call time"/>
                <input value={t.notes} onChange={e=>updTalent(t.id,'notes',e.target.value)} style={inp()} placeholder="Notes"/>
              </div>
            </div>
          ))}
        </Section>
        <Section title={`Looks / Outfits (${(session.outfits||[]).length})`} action={<Btn small gold onClick={addOutfit}>+ Add Look</Btn>}>
          {(session.outfits||[]).length===0 && <div style={{color:T.text3,fontSize:12,padding:'1rem',textAlign:'center'}}>No looks added yet.</div>}
          {(session.outfits||[]).map(o=>(
            <div key={o.id} style={{background:T.s3,borderRadius:8,padding:'10px',marginBottom:8}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                <input value={o.look} onChange={e=>updOutfit(o.id,'look',e.target.value)} style={inp()} placeholder="Look name / number"/>
                <input value={o.talent} onChange={e=>updOutfit(o.id,'talent',e.target.value)} style={inp()} placeholder="Talent name"/>
                <input value={o.order} onChange={e=>updOutfit(o.id,'order',e.target.value)} style={inp()} placeholder="Shoot order (#)"/>
                <input value={o.notes} onChange={e=>updOutfit(o.id,'notes',e.target.value)} style={inp()} placeholder="Wardrobe notes"/>
              </div>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

// ── BUDGET TAB ────────────────────────────────────────────────
function BudgetTab({board,setBoard}) {
  const [items,setItems] = useState(board.budgetItems||[]);
  const [currency,setCurrency] = useState(board.budget?.currency||'USD');
  const CATS = ['Crew','Cast','Equipment','Location','Travel','Catering','Post-Production','Music/Licensing','VFX','Insurance','Permits','Miscellaneous'];

  const save = (list) => { setItems(list); setBoard(b=>({...b,budgetItems:list})); };
  const add = () => save([...items,{id:Date.now(),category:'Crew',description:'',estimated:0,actual:0,paid:false,notes:''}]);
  const upd = (id,f,v) => save(items.map(i=>i.id===id?{...i,[f]:v}:i));
  const remove = (id) => save(items.filter(i=>i.id!==id));

  const totalEstimated = items.reduce((a,i)=>a+(parseFloat(i.estimated)||0),0);
  const totalActual = items.reduce((a,i)=>a+(parseFloat(i.actual)||0),0);
  const totalPaid = items.filter(i=>i.paid).reduce((a,i)=>a+(parseFloat(i.actual)||0),0);
  const variance = totalActual - totalEstimated;

  const fmt = (n) => new Intl.NumberFormat('en-US',{style:'currency',currency}).format(n);

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
        {[['Estimated',fmt(totalEstimated),T.text2],['Actual',fmt(totalActual),T.cream],['Paid',fmt(totalPaid),T.teal],['Variance',fmt(variance),variance>0?T.rust:T.teal]].map(([k,v,col])=>(
          <div key={k} style={{background:T.s2,border:`1px solid ${T.border}`,borderRadius:10,padding:'10px 14px'}}>
            <div style={{fontSize:10,color:T.text3,textTransform:'uppercase',letterSpacing:2}}>{k}</div>
            <div style={{fontSize:18,fontWeight:700,color:col,marginTop:2}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <select value={currency} onChange={e=>setCurrency(e.target.value)} style={{...sel(),width:'auto'}}>
          {['USD','EUR','GBP','CAD','AUD'].map(c=><option key={c}>{c}</option>)}
        </select>
        <Btn small gold onClick={add}>+ Add Line Item</Btn>
      </div>

      {items.length===0 && <div style={{padding:'3rem',textAlign:'center',color:T.text3,border:`1px dashed ${T.border2}`,borderRadius:12}}>No budget items yet. Add line items to track your production budget.</div>}

      <div style={{background:T.s2,borderRadius:12,overflow:'hidden',border:`1px solid ${T.border}`}}>
        {items.length>0 && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1.5fr 1fr 1fr 1fr auto',gap:0,padding:'8px 12px',borderBottom:`1px solid ${T.border}`,background:T.s3}}>
            {['Category','Description','Estimated','Actual','Paid',''].map(h=><div key={h} style={{fontSize:10,color:T.text3,textTransform:'uppercase',letterSpacing:1}}>{h}</div>)}
          </div>
        )}
        {items.map(item=>(
          <div key={item.id} style={{display:'grid',gridTemplateColumns:'1fr 1.5fr 1fr 1fr 1fr auto',gap:8,padding:'8px 12px',borderBottom:`1px solid ${T.border}`,alignItems:'center'}}>
            <select value={item.category} onChange={e=>upd(item.id,'category',e.target.value)} style={{...sel(),fontSize:11}}>{CATS.map(c=><option key={c}>{c}</option>)}</select>
            <input value={item.description} onChange={e=>upd(item.id,'description',e.target.value)} style={{...inp(),fontSize:11}} placeholder="Description"/>
            <input type="number" value={item.estimated} onChange={e=>upd(item.id,'estimated',e.target.value)} style={{...inp(),fontSize:11}} placeholder="0"/>
            <input type="number" value={item.actual} onChange={e=>upd(item.id,'actual',e.target.value)} style={{...inp(),fontSize:11}} placeholder="0"/>
            <label style={{display:'flex',gap:6,alignItems:'center',cursor:'pointer',fontSize:11,color:item.paid?T.teal:T.text2}}>
              <input type="checkbox" checked={!!item.paid} onChange={e=>upd(item.id,'paid',e.target.checked)} style={{accentColor:T.teal}}/>
              {item.paid?'Paid':'Unpaid'}
            </label>
            <button onClick={()=>remove(item.id)} style={{background:'none',border:'none',color:T.text3,cursor:'pointer',fontSize:14}}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SCHEDULE TAB ──────────────────────────────────────────────
function ScheduleTab({board,setBoard,scenes}) {
  const [days,setDays] = useState(board.shootDays||[]);
  const addDay = () => {
    const d={id:Date.now(),date:'',callTime:'06:00',wrapTime:'18:00',location:'',scenes:[],notes:''};
    const updated=[...days,d]; setDays(updated); setBoard(b=>({...b,shootDays:updated}));
  };
  const upd = (id,f,v) => {
    const updated=days.map(d=>d.id===id?{...d,[f]:v}:d); setDays(updated); setBoard(b=>({...b,shootDays:updated}));
  };
  const remove = (id) => { const updated=days.filter(d=>d.id!==id); setDays(updated); setBoard(b=>({...b,shootDays:updated})); };
  const totalHours = days.reduce((a,d)=>{ if(!d.callTime||!d.wrapTime) return a; const [ch,cm]=d.callTime.split(':').map(Number); const [wh,wm]=d.wrapTime.split(':').map(Number); return a+((wh*60+wm)-(ch*60+cm))/60; },0);

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
        {[['Shoot Days',days.length],['Total Hours',totalHours.toFixed(1)+'h'],['Scenes Scheduled',scenes.length]].map(([k,v])=>(
          <div key={k} style={{background:T.s2,border:`1px solid ${T.border}`,borderRadius:10,padding:'10px 14px'}}>
            <div style={{fontSize:10,color:T.text3,textTransform:'uppercase',letterSpacing:2}}>{k}</div>
            <div style={{fontSize:22,fontWeight:700,color:T.gold,marginTop:2}}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
        <Btn small gold onClick={addDay}>+ Add Shoot Day</Btn>
      </div>
      {days.length===0 && <div style={{padding:'3rem',textAlign:'center',color:T.text3,border:`1px dashed ${T.border2}`,borderRadius:12}}>No shoot days scheduled yet. Add shoot days to build your production schedule.</div>}
      {days.map((day,i)=>(
        <div key={day.id} style={{background:T.s2,border:`1px solid ${T.border2}`,borderRadius:12,padding:14,marginBottom:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:T.gold}}>Day {i+1}</div>
            <button onClick={()=>remove(day.id)} style={{background:'none',border:'none',color:T.text3,cursor:'pointer',fontSize:14}}>× Remove</button>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:8,marginBottom:8}}>
            <Field label="Date"><input type="date" value={day.date} onChange={e=>upd(day.id,'date',e.target.value)} style={inp()}/></Field>
            <Field label="Call Time"><input type="time" value={day.callTime} onChange={e=>upd(day.id,'callTime',e.target.value)} style={inp()}/></Field>
            <Field label="Wrap Time"><input type="time" value={day.wrapTime} onChange={e=>upd(day.id,'wrapTime',e.target.value)} style={inp()}/></Field>
            <Field label="Location"><input value={day.location} onChange={e=>upd(day.id,'location',e.target.value)} style={inp()} placeholder="Location name"/></Field>
          </div>
          <Field label="Scenes Scheduled">
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {scenes.map(s=>(
                <button key={s.id} onClick={()=>{ const sc=day.scenes.includes(s.id)?day.scenes.filter(x=>x!==s.id):[...day.scenes,s.id]; upd(day.id,'scenes',sc); }} style={{background:day.scenes.includes(s.id)?T.gold:T.s3,color:day.scenes.includes(s.id)?T.bg:T.text2,border:`1px solid ${day.scenes.includes(s.id)?T.gold:T.border2}`,borderRadius:6,padding:'3px 10px',fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>
                  Scene {s.num}
                </button>
              ))}
              {scenes.length===0 && <span style={{fontSize:11,color:T.text3}}>Add scenes in Scenes + Shots tab first</span>}
            </div>
          </Field>
          <Field label="Day Notes">
            <textarea value={day.notes} onChange={e=>upd(day.id,'notes',e.target.value)} style={{...inp(),height:50,resize:'vertical'}} placeholder="Special requirements, permits, weather contingency..."/>
          </Field>
        </div>
      ))}
    </div>
  );
}

// ── SCRIPT TAB ────────────────────────────────────────────────
function ScriptTab({board,setBoard,scenes,setScenes}) {
  const [scriptText,setScriptText] = useState(board.scriptText||'');
  const [parsing,setParsing] = useState(false);

  const saveScript = (text) => { setScriptText(text); setBoard(b=>({...b,scriptText:text})); };

  const parseScript = () => {
    setParsing(true);
    // Basic scene heading detection
    const lines = scriptText.split('\n');
    const newScenes = [];
    let current = null;
    lines.forEach(line => {
      const trimmed = line.trim();
      if (/^(INT|EXT|INT\/EXT|EXT\/INT)\./i.test(trimmed)) {
        if (current) newScenes.push(current);
        current = { id:`s${Date.now()}${newScenes.length}`, num:String(newScenes.length+1).padStart(2,'0'), name:trimmed, characters:[], set:'', props:[], lighting:'', hairMakeup:'', wardrobe:'', weather:'', vfxNotes:'', musicNotes:'', continuityFlags:'', clientStatus:'Pending', inspirationUrls:[], shots:[{id:`sh${Date.now()}`,num:'01',type:'WIDE',desc:'',tags:[],notes:'',cameraMove:'Static',lens:'35mm',iso:'',aperture:'',shutter:'',lighting:'',materials:'',positioning:'',vfxNotes:'',clientStatus:'Pending',inspirationUrls:[],sketches:[],sketchAsImage:null}] };
      } else if (current && /^[A-Z]{2,}(\s+[A-Z]+)*$/.test(trimmed) && trimmed.length < 40) {
        if (!current.characters.includes(trimmed)) current.characters = [...current.characters, trimmed];
      }
    });
    if (current) newScenes.push(current);
    if (newScenes.length > 0) {
      setScenes(newScenes);
      dispatch_note = newScenes.length + ' scenes extracted';
    }
    setParsing(false);
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.cream}}>Script</div>
          <div style={{fontSize:11,color:T.text2}}>Paste your script and extract scenes automatically.</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <Btn small onClick={parseScript} disabled={!scriptText.trim()||parsing}>{parsing?'Parsing...':'Extract Scenes'}</Btn>
          <Btn small gold onClick={()=>saveScript(scriptText)}>Save</Btn>
        </div>
      </div>
      {scenes.length>0 && (
        <div style={{background:'rgba(26,138,118,0.08)',border:`1px solid ${T.teal}30`,borderRadius:10,padding:10,marginBottom:12,fontSize:11,color:T.teal}}>
          ✓ {scenes.length} scenes extracted and linked to Scenes + Shots tab.
        </div>
      )}
      <textarea
        value={scriptText}
        onChange={e=>saveScript(e.target.value)}
        style={{...inp(),height:'60vh',resize:'vertical',fontFamily:'monospace',fontSize:12,lineHeight:1.8}}
        placeholder={'Paste your script here...\n\nINT. LOCATION — DAY\n\nAction description.\n\nCHARACTER\nDialogue.'}
      />
    </div>
  );
}

// ── EXPORTS TAB ───────────────────────────────────────────────
function ExportsTab({board,scenes}) {
  const totalShots = scenes.reduce((a,s)=>a+s.shots.length,0);

  const generateShotList = () => {
    let text = `SHOT LIST — ${board.title}\n`;
    text += `${board.type} | ${board.platform}\n`;
    text += `Director: ${board.director||'—'} | Date: ${board.shootDate||'—'} | Location: ${board.location||'—'}\n`;
    text += '='.repeat(60)+'\n\n';
    scenes.forEach(scene => {
      text += `SCENE ${scene.num}: ${scene.name}\n`;
      if(scene.characters?.length) text += `Characters: ${scene.characters.join(', ')}\n`;
      if(scene.set) text += `Set: ${scene.set}\n`;
      text += '\n';
      scene.shots.forEach(shot => {
        text += `  Shot ${shot.num} | ${shot.type} | ${shot.cameraMove||'Static'} | ${shot.lens||'—'}\n`;
        if(shot.desc) text += `  ${shot.desc}\n`;
        if(shot.lighting) text += `  Lighting: ${shot.lighting}\n`;
        if(shot.notes) text += `  Notes: ${shot.notes}\n`;
        text += '\n';
      });
      text += '-'.repeat(40)+'\n\n';
    });
    const blob = new Blob([text],{type:'text/plain'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download=`${board.title.replace(/\s+/g,'_')}_ShotList.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const generateCallSheet = () => {
    let text = `CALL SHEET — ${board.title}\n`;
    text += `Date: ${board.shootDate||'TBD'} | Location: ${board.location||'TBD'}\n`;
    text += `Director: ${board.director||'—'} | ${board.type}\n`;
    text += '='.repeat(60)+'\n\n';
    text += `PRODUCTION NOTES:\n${board.productionNotes||'None'}\n\n`;
    text += `SAFETY NOTES:\n${board.safetyNotes||'None'}\n\n`;
    text += `SCENES TODAY:\n`;
    scenes.forEach(s => { text += `  Scene ${s.num}: ${s.name} (${s.shots.length} shots)\n`; });
    const blob = new Blob([text],{type:'text/plain'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download=`${board.title.replace(/\s+/g,'_')}_CallSheet.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const data = {board,scenes,exported:new Date().toISOString()};
    const blob = new Blob([JSON.stringify(data,null,2)],{type:'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download=`${board.title.replace(/\s+/g,'_')}_VizionBoard.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:24}}>
        {[['Scenes',scenes.length],['Shots',totalShots],['Board Type',board.type]].map(([k,v])=>(
          <div key={k} style={{background:T.s2,border:`1px solid ${T.border}`,borderRadius:10,padding:'10px 14px'}}>
            <div style={{fontSize:10,color:T.text3,textTransform:'uppercase',letterSpacing:2}}>{k}</div>
            <div style={{fontSize:22,fontWeight:700,color:T.gold,marginTop:2}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {[
          {title:'Shot List',icon:'📋',desc:'Complete shot-by-shot breakdown with scene headings, shot types, camera specs, and director notes. Ready to print or share with crew.',action:generateShotList,label:'Download Shot List'},
          {title:'Call Sheet',icon:'📄',desc:'Production call sheet with scene schedule, location, safety notes, and production info. Share with cast and crew before shoot day.',action:generateCallSheet,label:'Download Call Sheet'},
          {title:'Board Export (JSON)',icon:'💾',desc:'Export your complete Vizion board as a JSON file. Import it back into Vizion or share the raw data with collaborators.',action:exportJSON,label:'Export JSON'},
          {title:'PDF Export',icon:'📑',desc:'Full visual PDF with shot thumbnails, scene breakdowns, and board overview. Coming in the next build pass.',action:null,label:'Coming Soon'},
        ].map(exp=>(
          <div key={exp.title} style={{background:T.s2,border:`1px solid ${T.border2}`,borderRadius:12,padding:20}}>
            <div style={{fontSize:24,marginBottom:8}}>{exp.icon}</div>
            <div style={{fontSize:14,fontWeight:700,color:T.cream,marginBottom:6}}>{exp.title}</div>
            <div style={{fontSize:12,color:T.text2,lineHeight:1.7,marginBottom:14}}>{exp.desc}</div>
            <button onClick={exp.action||undefined} disabled={!exp.action} style={{background:exp.action?`linear-gradient(135deg,${T.gold},${T.gold2})`:T.s3,border:exp.action?'none':`1px solid ${T.border2}`,borderRadius:8,padding:'8px 16px',fontSize:12,fontWeight:exp.action?700:400,color:exp.action?T.bg:T.text3,cursor:exp.action?'pointer':'default',fontFamily:'inherit',width:'100%',opacity:exp.action?1:0.6}}>
              {exp.label}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VizionView() {
  const [board,setBoard] = useState(INIT_BOARD);
  const [scenes,setScenes] = useState([]);
  const [activeTab,setActiveTab] = useState('overview');
  const [saved,setSaved] = useState(false);
  const tabs = [
    {id:'overview',label:'Overview'},
    {id:'scenes',label:'Scenes + Shots'},
    {id:'inspiration',label:'Inspiration'},
    {id:'photo',label:'Photo Session'},
    {id:'budget',label:'Budget'},
    {id:'schedule',label:'Schedule'},
    {id:'script',label:'Script'},
    {id:'sag',label:'SAG / Union'},
    {id:'team',label:'Team'},
    {id:'exports',label:'Exports'},
  ];
  // handleSave defined above with Supabase
  return (
    <div style={{height:'100%',display:'flex',flexDirection:'column',background:T.bg,fontFamily:"'DM Sans','Jost',sans-serif",overflow:'hidden'}}>
      <div style={{padding:'12px 20px',borderBottom:`1px solid ${T.border}`,background:T.s1,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <input value={board.title} onChange={e=>setBoard(b=>({...b,title:e.target.value}))} style={{background:'none',border:'none',color:T.cream,fontSize:18,fontWeight:700,outline:'none',fontFamily:'inherit',padding:0,width:'300px'}}/>
          <div style={{fontSize:11,color:T.text3,marginTop:2}}>{board.type} · {board.platform?.split(' (')[0]||''}</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {saved && <span style={{fontSize:11,color:T.teal}}>✓ Saved</span>}
          {saving && <span style={{fontSize:11,color:T.gold}}>Saving...</span>}
          <Btn small onClick={newBoard}>+ New</Btn>
          <Btn small onClick={handleSave}>{saving?'Saving...':'Save'}</Btn>
          <Btn small gold onClick={()=>alert('Export — coming in Pass 2')}>Export</Btn>
        </div>
      </div>
      <div style={{display:'flex',borderBottom:`1px solid ${T.border}`,background:T.s1,flexShrink:0,paddingLeft:20,gap:0,overflowX:'auto'}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{background:'none',border:'none',borderBottom:activeTab===t.id?`2px solid ${T.gold}`:'2px solid transparent',color:activeTab===t.id?T.gold:T.text2,padding:'10px 16px',fontSize:12,fontWeight:activeTab===t.id?700:400,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>{t.label}</button>
        ))}
        <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:8,paddingRight:12,fontSize:10,color:T.text3}}>
          <span>Scenes: {scenes.length}</span><span>·</span><span>Shots: {scenes.reduce((a,s)=>a+s.shots.length,0)}</span>
        </div>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'20px'}}>
        {activeTab==='overview' && <OverviewTab board={board} setBoard={setBoard} scenes={scenes}/>}
        {activeTab==='scenes' && <ScenesTab scenes={scenes} setScenes={setScenes}/>}
        {activeTab==='inspiration' && <InspirationTab board={board} setBoard={setBoard}/>}
        {activeTab==='photo' && <PhotoSessionTab board={board} setBoard={setBoard}/>}
        {activeTab==='budget' && <BudgetTab board={board} setBoard={setBoard}/>}
        {activeTab==='schedule' && <ScheduleTab board={board} setBoard={setBoard} scenes={scenes}/>}
        {activeTab==='script' && <ScriptTab board={board} setBoard={setBoard} scenes={scenes} setScenes={setScenes}/>}
        {activeTab==='sag' && <SagTab board={board} setBoard={setBoard}/>}
        {activeTab==='team' && <TeamTab board={board} setBoard={setBoard}/>}
        {activeTab==='exports' && <ExportsTab board={board} scenes={scenes}/>}
      </div>
    </div>
  );
}
